import {forwardRef} from "react";
import {css, Interpolation, Theme} from "@emotion/react";
import styled from "@emotion/styled";

import {borderRadius, flex, pl, pr} from "@pg-design/helpers-css";
import {MapMarkerOutlineIcon} from "@pg-design/icons-module";
import {FieldMessage, IInputCoreProps, InputCore, InputWrapper} from "@pg-design/inputs-module";
import {Pill} from "@pg-design/pill-module";
import {Text} from "@pg-design/text-module";
import {NAVIGATION_TEST_IDS} from "@pg-mono/test-ids";

import {SearchDropdownArrow} from "../atoms/SearchDropdownArrow";
import {ISearchAutocompleteInputProps} from "./SearchAutocompleteTypes";
import {useSearchAutocompleteInput} from "./useSearchAutocompleteInput";

export const SearchAutocompleteInput = (props: ISearchAutocompleteInputProps) => {
    const input = useSearchAutocompleteInput(props);

    /**
     * Render
     */

    return (
        <div onClick={input.onDivClick} css={[wrapperStyle, props.inputWrapperCss]}>
            <InputContainerStyle hasError={Boolean(props.error)} css={[props.inputContainerCss]}>
                <div css={[iconWrapper, props.iconWrapperCss]}>{props.inputIcon ?? <MapMarkerOutlineIcon size="2.4" />}</div>

                {props.regionPillLabels.length > 0 && !props.isTravelTimeTabActive && (
                    <div css={[regionPillWrapper, props.regionPillWrapperCss]}>
                        {props.regionPillLabels.map((regionPillLabel, index) => (
                            <Pill
                                key={`${regionPillLabel}-${index.toString()}`}
                                onCloseClick={index === props.regionPillLabels.length - 1 ? props.onRemoveAllRegions : undefined}
                                size="small"
                            >
                                <Text variant="info_txt_1" noWrap css={pillLabelStyles}>
                                    {regionPillLabel}
                                </Text>
                            </Pill>
                        ))}
                    </div>
                )}
                <SearchAutocompleteInputCore
                    ref={input.inputRef}
                    autoComplete="off"
                    name={props.name}
                    value={input.inputValue}
                    placeholder={props.placeholder}
                    onChange={input.onChange}
                    onMouseUp={input.onMouseUp}
                    readOnly={props.readOnly}
                    autoFocus={props.autoFocus}
                    hasDistanceFilter={!!props.isInputWithDistanceFilter}
                    disabled={props.inputDisabled}
                    isTravelTimeTabActive={props.isTravelTimeTabActive}
                    css={props.inputCss}
                    inputWrapperCss={props.inputCoreWrapperCss}
                />

                {!props.hideArrowIcon && (
                    <ArrowWrapper onClick={input.onIconClick} showOnMobile={props.showArrowOnMobile}>
                        {props.dropdownIcon || <SearchDropdownArrow isDown={props.isDropdownOpen} />}
                    </ArrowWrapper>
                )}
            </InputContainerStyle>
            {props.error ? <FieldMessage fieldState="error">{props.error}</FieldMessage> : null}
        </div>
    );
};

type ISearchAutocompleteInputCore = IInputCoreProps & {
    hasDistanceFilter: boolean;
    isTravelTimeTabActive?: boolean;
    inputWrapperCss?: Interpolation<Theme>;
};
export const SearchAutocompleteInputCore = forwardRef<HTMLInputElement, ISearchAutocompleteInputCore>((props, forwardedRef) => {
    return (
        <StyledInputWrapper className={props.className} hasDistanceFilter={!!props.hasDistanceFilter} css={props.inputWrapperCss}>
            <InputCore
                dataTestId={NAVIGATION_TEST_IDS.SEARCH_BAR.INPUT}
                ref={forwardedRef}
                autoComplete="off"
                name={props.name}
                value={props.value}
                placeholder={props.isTravelTimeTabActive ? "Wpisz dokładny adres" : props.placeholder}
                onChange={props.onChange}
                onMouseUp={props.onMouseUp}
                readOnly={props.readOnly}
                autoFocus={props.autoFocus}
                disabled={props.disabled}
                css={textOverflowStyle}
            />
        </StyledInputWrapper>
    );
});

const regionPillWrapper = (theme: Theme) => css`
    position: relative;
    display: none;
    gap: 0.4rem;
    ${pr(2)};

    @media (min-width: ${theme.breakpoints.md}) {
        display: flex;
    }
`;

const pillLabelStyles = css`
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    max-width: 100px;
`;

const wrapperStyle = css`
    position: relative;
    z-index: 1001;
    width: 100%;
`;

const InputContainerStyle = styled.div<{hasError?: boolean}>`
    ${flex("center", "flex-start")};
    background-color: #fff;
    border: 1px solid ${({theme, hasError}) => (hasError ? theme.colors.danger : theme.colors.gray["600"])};
    height: 50px;
    ${borderRadius()};
`;

export const iconWrapper = css`
    flex: 0 0 auto;
    cursor: text;
    z-index: 1;
    height: 100%;
    width: 4.8rem;
    ${pl(2)};
    ${pr(1)};
    display: flex;
    justify-content: center;
    align-items: center;
`;

interface ArrowWrapperProps {
    showOnMobile?: boolean;
}

const arrowWrapperStyle = css`
    position: absolute;
    display: block;
    top: 1.5rem;
    right: 2.5rem;
    cursor: pointer;
    z-index: 1;
`;

export const ArrowWrapper = styled.div<ArrowWrapperProps>`
    display: none;
    ${(props) =>
        props.showOnMobile &&
        css`
            ${arrowWrapperStyle};
            right: 1.6rem;
        `};

    @media (min-width: ${(props) => props.theme.breakpoints.md}) {
        ${arrowWrapperStyle};
    }
`;

interface IStyledInputWrapper {
    hasDistanceFilter: boolean;
}
export const StyledInputWrapper = styled(InputWrapper)<IStyledInputWrapper>`
    flex-basis: 100%;
    width: 100%;
    ${pr(2)};
    ${pl(0)};
    border: none;

    ${({hasDistanceFilter}) =>
        hasDistanceFilter &&
        css`
            border-top-left-radius: 0;
            border-bottom-left-radius: 0;
            margin-bottom: 0;
            width: 100%;
            z-index: initial;
        `};

    @media (min-width: ${(props) => props.theme.breakpoints.md}) {
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
    }

    &::placeholder {
        color: ${(props) => props.theme.colors.gray[700]};
    }

    &:focus {
        outline: none;
    }

    &:hover {
        box-shadow: none;
    }

    & > div {
        max-width: 80%;
    }
`;

const textOverflowStyle = css`
    text-overflow: ellipsis;
`;
import {forwardRef} from "react";
import {css, Interpolation, Theme} from "@emotion/react";
import styled from "@emotion/styled";

import {borderRadius, flex, pl, pr} from "@pg-design/helpers-css";
import {MapMarkerOutlineIcon} from "@pg-design/icons-module";
import {FieldMessage, IInputCoreProps, InputCore, InputWrapper} from "@pg-design/inputs-module";
import {Pill} from "@pg-design/pill-module";
import {Text} from "@pg-design/text-module";
import {NAVIGATION_TEST_IDS} from "@pg-mono/test-ids";

import {SearchDropdownArrow} from "../atoms/SearchDropdownArrow";
import {ISearchAutocompleteInputProps} from "./SearchAutocompleteTypes";
import {useSearchAutocompleteInput} from "./useSearchAutocompleteInput";

export const SearchAutocompleteInput = (props: ISearchAutocompleteInputProps) => {
    const input = useSearchAutocompleteInput(props);

    /**
     * Render
     */

    return (
        <div onClick={input.onDivClick} css={[wrapperStyle, props.inputWrapperCss]}>
            <InputContainerStyle hasError={Boolean(props.error)} css={[props.inputContainerCss]}>
                <div css={[iconWrapper, props.iconWrapperCss]}>{props.inputIcon ?? <MapMarkerOutlineIcon size="2.4" />}</div>

                {props.regionPillLabels.length > 0 && !props.isTravelTimeTabActive && (
                    <div css={[regionPillWrapper, props.regionPillWrapperCss]}>
                        {props.regionPillLabels.map((regionPillLabel, index) => (
                            <Pill
                                key={`${regionPillLabel}-${index.toString()}`}
                                onCloseClick={index === props.regionPillLabels.length - 1 ? props.onRemoveAllRegions : undefined}
                                size="small"
                            >
                                <Text variant="info_txt_1" noWrap css={pillLabelStyles}>
                                    {regionPillLabel}
                                </Text>
                            </Pill>
                        ))}
                    </div>
                )}
                <SearchAutocompleteInputCore
                    ref={input.inputRef}
                    autoComplete="off"
                    name={props.name}
                    value={input.inputValue}
                    placeholder={props.placeholder}
                    onChange={input.onChange}
                    onMouseUp={input.onMouseUp}
                    readOnly={props.readOnly}
                    autoFocus={props.autoFocus}
                    hasDistanceFilter={!!props.isInputWithDistanceFilter}
                    disabled={props.inputDisabled}
                    isTravelTimeTabActive={props.isTravelTimeTabActive}
                    css={props.inputCss}
                    inputWrapperCss={props.inputCoreWrapperCss}
                />

                {!props.hideArrowIcon && (
                    <ArrowWrapper onClick={input.onIconClick} showOnMobile={props.showArrowOnMobile}>
                        {props.dropdownIcon || <SearchDropdownArrow isDown={props.isDropdownOpen} />}
                    </ArrowWrapper>
                )}
            </InputContainerStyle>
            {props.error ? <FieldMessage fieldState="error">{props.error}</FieldMessage> : null}
        </div>
    );
};

type ISearchAutocompleteInputCore = IInputCoreProps & {
    hasDistanceFilter: boolean;
    isTravelTimeTabActive?: boolean;
    inputWrapperCss?: Interpolation<Theme>;
};
export const SearchAutocompleteInputCore = forwardRef<HTMLInputElement, ISearchAutocompleteInputCore>((props, forwardedRef) => {
    return (
        <StyledInputWrapper className={props.className} hasDistanceFilter={!!props.hasDistanceFilter} css={props.inputWrapperCss}>
            <InputCore
                dataTestId={NAVIGATION_TEST_IDS.SEARCH_BAR.INPUT}
                ref={forwardedRef}
                autoComplete="off"
                name={props.name}
                value={props.value}
                placeholder={props.isTravelTimeTabActive ? "Wpisz dokładny adres" : props.placeholder}
                onChange={props.onChange}
                onMouseUp={props.onMouseUp}
                readOnly={props.readOnly}
                autoFocus={props.autoFocus}
                disabled={props.disabled}
                css={textOverflowStyle}
            />
        </StyledInputWrapper>
    );
});

const regionPillWrapper = (theme: Theme) => css`
    position: relative;
    display: none;
    gap: 0.4rem;
    ${pr(2)};

    @media (min-width: ${theme.breakpoints.md}) {
        display: flex;
    }
`;

const pillLabelStyles = css`
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    max-width: 100px;
`;

const wrapperStyle = css`
    position: relative;
    z-index: 1001;
    width: 100%;
`;

const InputContainerStyle = styled.div<{hasError?: boolean}>`
    ${flex("center", "flex-start")};
    background-color: #fff;
    border: 1px solid ${({theme, hasError}) => (hasError ? theme.colors.danger : theme.colors.gray["600"])};
    height: 50px;
    ${borderRadius()};
`;

export const iconWrapper = css`
    flex: 0 0 auto;
    cursor: text;
    z-index: 1;
    height: 100%;
    width: 4.8rem;
    ${pl(2)};
    ${pr(1)};
    display: flex;
    justify-content: center;
    align-items: center;
`;

interface ArrowWrapperProps {
    showOnMobile?: boolean;
}

const arrowWrapperStyle = css`
    position: absolute;
    display: block;
    top: 1.5rem;
    right: 2.5rem;
    cursor: pointer;
    z-index: 1;
`;

export const ArrowWrapper = styled.div<ArrowWrapperProps>`
    display: none;
    ${(props) =>
        props.showOnMobile &&
        css`
            ${arrowWrapperStyle};
            right: 1.6rem;
        `};

    @media (min-width: ${(props) => props.theme.breakpoints.md}) {
        ${arrowWrapperStyle};
    }
`;

interface IStyledInputWrapper {
    hasDistanceFilter: boolean;
}
export const StyledInputWrapper = styled(InputWrapper)<IStyledInputWrapper>`
    flex-basis: 100%;
    width: 100%;
    ${pr(2)};
    ${pl(0)};
    border: none;

    ${({hasDistanceFilter}) =>
        hasDistanceFilter &&
        css`
            border-top-left-radius: 0;
            border-bottom-left-radius: 0;
            margin-bottom: 0;
            width: 100%;
            z-index: initial;
        `};

    @media (min-width: ${(props) => props.theme.breakpoints.md}) {
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
    }

    &::placeholder {
        color: ${(props) => props.theme.colors.gray[700]};
    }

    &:focus {
        outline: none;
    }

    &:hover {
        box-shadow: none;
    }

    & > div {
        max-width: 80%;
    }
`;

const textOverflowStyle = css`
    text-overflow: ellipsis;
`;
